/**
 * Loads the content editors manage in the admin and swaps it into the site's
 * data modules. The first render waits a short moment for it; if the API is
 * slower than that (e.g. a cold serverless start), the site renders its
 * built-in content and re-renders as soon as the admin's content arrives.
 */
import { API_BASE } from '../lib/apiBase';
import { applyRemoteProducts, applyRemoteTds, type RemoteProduct } from './catalog';
import { EVENTS } from './productMedia';
import { heroSlides, type HeroSlide } from './homeImages';
import { MARMOVELLA, type MarmovellaFamily } from './marmovella';
import { SITE, type SiteInfo } from './site';

type PublicContent = {
  products: RemoteProduct[] | null;
  tdsDocs: { name: string; url: string }[] | null;
  events: { title: string; date: string; place: string | null; host: string | null; image: string | null; alt: string | null }[] | null;
  heroSlides: HeroSlide[] | null;
  marmovella: MarmovellaFamily[] | null;
  site: SiteInfo | null;
};

/** Fired when admin content arrives after the first render; the app re-renders on it. */
export const CONTENT_EVENT = 'vp:content-updated';

function apply(c: PublicContent) {
  if (c.products?.length) applyRemoteProducts(c.products);
  if (c.tdsDocs) applyRemoteTds(c.tdsDocs);
  if (c.events) {
    EVENTS.splice(
      0,
      EVENTS.length,
      ...c.events.filter((e) => e.image).map((e) => ({ title: e.title, date: e.date, place: e.place || '', host: e.host || '', image: e.image as string, alt: e.alt || e.title }))
    );
  }
  if (c.heroSlides?.length) heroSlides.splice(0, heroSlides.length, ...c.heroSlides);
  if (c.marmovella?.length === 3) MARMOVELLA.splice(0, MARMOVELLA.length, ...c.marmovella);
  if (c.site) Object.assign(SITE, c.site, { social: { ...SITE.social, ...c.site.social } });
}

export async function loadRemoteContent(waitMs = 2500): Promise<void> {
  // The admin screens read the database directly; only the public site needs this.
  if (typeof window === 'undefined' || window.location.pathname.startsWith('/admin')) return;

  let rendered = false;
  // no-cache: always revalidate, so an edit is never hidden by the browser's own cache.
  const request = fetch(`${API_BASE}/api/public/content`, { cache: 'no-cache' })
    .then((r) => (r.ok ? (r.json() as Promise<PublicContent>) : null))
    .then((c) => {
      if (!c) return;
      apply(c);
      if (rendered) window.dispatchEvent(new Event(CONTENT_EVENT));
    })
    .catch(() => {
      /* offline or API down: keep the built-in content */
    });

  await Promise.race([request, new Promise((r) => setTimeout(r, waitMs))]);
  rendered = true;
}
